import { useCallback, useEffect, useState } from 'react';
import { loadRecentSearches, peekRecentSearches, saveRecentSearch } from './recentSearches';

/**
 * Each person's recent picks, per kind of picker: the last few people,
 * projects, chats or vendors they chose, shown first the next time.
 *
 * Kept with the recent searches (server/routes/recentSearches.js) under the
 * name `pick.<kind>`, holding ids instead of words, so the website and the app
 * share them. Pickers of the same kind share one list ('task-assignee' covers
 * every task form, 'project' every project picker and filter), and the app
 * uses the same names (mobile/src/services/recentPicks.ts).
 *
 *   const recent = useRecentPicks('project');
 *   recent.order(projects)            // recent ones first, then the rest as before
 *   recent.split(people)              // { recent, rest } for a "Recent" heading
 *   recent.record(project._id)        // when one is picked
 *
 * The order is read when the picker opens (`active`) and then held, so the
 * list does not reshuffle under the person while they are picking.
 */
const scopeOf = (kind) => `pick.${kind}`;

const idReader = (idOf) => (typeof idOf === 'function' ? idOf : (item) => (item == null ? '' : item[idOf]));

/** Splits `items` into the recent ones (newest first) and the rest, in their own order. */
export const splitRecent = (items, ids, idOf = '_id') => {
    const list = items || [];
    if (!ids?.length || !list.length) return { recent: [], rest: list };
    const read = idReader(idOf);
    const byId = new Map(list.map((item) => [String(read(item)), item]));
    const recent = [];
    ids.forEach((id) => {
        const item = byId.get(String(id));
        if (item !== undefined && !recent.includes(item)) recent.push(item);
    });
    if (!recent.length) return { recent, rest: list };
    const taken = new Set(recent);
    return { recent, rest: list.filter((item) => !taken.has(item)) };
};

export const recentFirst = (items, ids, idOf = '_id') => {
    const { recent, rest } = splitRecent(items, ids, idOf);
    return recent.length ? [...recent, ...rest] : (items || []);
};

/**
 * Saves a pick. Never throws; a failed save only means it is not remembered.
 * A filter's "All" choice and an empty choice are not picks, so are skipped.
 */
export const recordRecentPick = (kind, id) => {
    const value = String(id ?? '').trim();
    if (value && value !== 'All') saveRecentSearch(scopeOf(kind), value);
};

export function useRecentPicks(kind, active = true) {
    const scope = scopeOf(kind);
    const [ids, setIds] = useState(() => peekRecentSearches(scope));

    useEffect(() => {
        if (!kind || !active) return undefined;
        let live = true;
        setIds(peekRecentSearches(scope));
        loadRecentSearches(scope).then((terms) => { if (live) setIds(terms); });
        return () => { live = false; };
    }, [kind, scope, active]);

    const record = useCallback((id) => { if (kind) recordRecentPick(kind, id); }, [kind]);
    const order = useCallback((items, idOf) => (kind ? recentFirst(items, ids, idOf) : (items || [])), [kind, ids]);
    const split = useCallback((items, idOf) => (kind ? splitRecent(items, ids, idOf) : { recent: [], rest: items || [] }), [kind, ids]);

    // For react-select: the recent ones under a "Recent" heading, then the rest.
    const groups = useCallback((options, idOf = 'value') => {
        const { recent, rest } = splitRecent(options, kind ? ids : [], idOf);
        return recent.length ? [{ label: 'Recent', options: recent }, { label: 'Everyone else', options: rest }] : (options || []);
    }, [kind, ids]);

    return { ids: kind ? ids : [], record, order, split, groups };
}
